/**
 * Structured Logger Utility
 * Development-only: log, debug. Always emitted: error, warn, info. Production errors via PostHog when registered.
 */

interface ImportMetaEnv {
  DEV?: boolean;
  PROD?: boolean;
}

export interface LogContext {
  module: string;
  action: string;
  correlationId?: string;
  sessionId?: string;
  [key: string]: unknown;
}

interface ParsedLogArgs {
  message: string;
  error?: Error;
  context: Record<string, unknown>;
}

const viteDev =
  typeof import.meta !== 'undefined' && (import.meta as { env?: ImportMetaEnv }).env?.DEV === true;
const nodeDev =
  typeof process !== 'undefined' && process?.env?.NODE_ENV !== 'production';
const isDev = viteDev || nodeDev;

let posthogInstance: {
  captureException?: (error: Error, properties?: Record<string, unknown>) => void;
  capture?: (event: string, properties?: Record<string, unknown>) => void;
} | null = null;

let posthogFailureCount = 0;
const MAX_FAILURE_COUNT = 5;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeMessage(firstArg: unknown): string {
  if (typeof firstArg === 'string') return firstArg;
  if (firstArg instanceof Error) return firstArg.message;
  return 'Application log';
}

function parseLogArgs(args: unknown[]): ParsedLogArgs {
  const [first, second, third, ...rest] = args;
  const message = normalizeMessage(first);

  let error: Error | undefined;
  let context: Record<string, unknown> = {};

  if (first instanceof Error) {
    error = first;
  }

  if (second instanceof Error) {
    error = second;
  } else if (isRecord(second)) {
    context = { ...context, ...second };
  }

  if (isRecord(third)) {
    context = { ...context, ...third };
  }

  if (rest.length > 0) {
    context.extra = rest;
  }

  return { message, error, context };
}

function emitToConsole(method: 'debug' | 'info' | 'log' | 'warn' | 'error', payload: ParsedLogArgs): void {
  if (!isDev && (method === 'debug' || method === 'log')) {
    return;
  }

  const consoleMethod = method === 'log' ? 'log' : method;

  // Emit as (message, context?, error?) separate args so:
  //  - Native DevTools still renders each object as an expandable tree.
  //  - Remote debugging bridges (e.g. Chrome DevTools Protocol / Cursor
  //    browser) show the human-readable message instead of stringifying
  //    everything to "[object Object]".
  const extras: unknown[] = [];
  if (Object.keys(payload.context).length > 0) {
    extras.push(payload.context);
  }
  if (payload.error) {
    extras.push({
      name: payload.error.name,
      message: payload.error.message,
      stack: payload.error.stack,
    });
  }

  if (extras.length === 0) {
    console[consoleMethod](payload.message);
  } else {
    console[consoleMethod](payload.message, ...extras);
  }
}

export function registerPostHog(posthog: {
  captureException?: (error: Error, properties?: Record<string, unknown>) => void;
  capture?: (event: string, properties?: Record<string, unknown>) => void;
} | null) {
  posthogInstance = posthog;
  posthogFailureCount = 0;
}

function getPostHog() {
  if (posthogInstance) {
    return posthogInstance;
  }

  try {
    if (typeof window !== 'undefined') {
      const w = window as unknown as { posthog?: { captureException?: (error: Error, properties?: Record<string, unknown>) => void } };
      if (w.posthog && typeof w.posthog.captureException === 'function') {
        return w.posthog;
      }
    }
  } catch {
    // no-op
  }

  return null;
}

function shouldUsePostHog(): boolean {
  if (posthogFailureCount >= MAX_FAILURE_COUNT) {
    return false;
  }

  const posthog = getPostHog();
  return posthog !== null && typeof posthog.captureException === 'function';
}

function captureErrorToPostHog(payload: ParsedLogArgs): void {
  if (!shouldUsePostHog()) {
    return;
  }

  const posthog = getPostHog();
  if (!posthog || typeof posthog.captureException !== 'function') {
    return;
  }

  try {
    const error = payload.error ?? new Error(payload.message);
    posthog.captureException(error, {
      log_level: 'error',
      message: payload.message,
      timestamp: new Date().toISOString(),
      ...payload.context,
    });
    posthogFailureCount = 0;
  } catch (captureError) {
    posthogFailureCount += 1;
    emitToConsole('error', {
      message: '[PostHog] Failed to capture exception',
      error: captureError instanceof Error ? captureError : undefined,
      context: {
        module: 'logger',
        action: 'capture_exception',
        failureCount: posthogFailureCount,
        originalMessage: payload.message,
      },
    });
  }
}

export const logger = {
  log: (...args: unknown[]) => {
    emitToConsole('log', parseLogArgs(args));
  },
  info: (...args: unknown[]) => {
    emitToConsole('info', parseLogArgs(args));
  },
  debug: (...args: unknown[]) => {
    emitToConsole('debug', parseLogArgs(args));
  },
  warn: (...args: unknown[]) => {
    emitToConsole('warn', parseLogArgs(args));
  },
  error: (...args: unknown[]) => {
    const payload = parseLogArgs(args);
    emitToConsole('error', payload);
    captureErrorToPostHog(payload);
  },
};
